import "./facets";
import "./filter-drawer";
import "./filter-count";

const COLUMNS_KEY = "plp-grid-columns";
const DEFAULT_COLUMNS = "4";
const INFINITE_KEY = "plp-infinite-scroll";

const PRODUCT_CARD_SELECTOR = "product-card, [data-product-card]";
const KBD_STOP_SELECTOR = "[data-kbd-secondary]";
const KBD_PRIMARY_SELECTOR = "[data-kbd-primary]";

function setKbdView(card, view) {
  if (!card) return;
  card.setAttribute("data-kbd-view", String(view));
}

function getCardFromTarget(target) {
  return target?.closest?.(PRODUCT_CARD_SELECTOR);
}

class MainCollection extends HTMLElement {
  constructor() {
    super();

    this.loadMoreWrapper = this.querySelector(".main-collection__load-more-wrapper");
    this._isLoadingMore = false;
    this._keyboardModeApplied = false;

    // Default: infinite ON for mouse/touch users
    this._infiniteEnabled = true;

    // Respect reduced motion: default OFF (still allow toggle + Load more)
    if (window.matchMedia("prefers-reduced-motion").matches) {
      this._infiniteEnabled = false;
    }

    // Restore saved preference if any
    try {
      const saved = window.localStorage.getItem(INFINITE_KEY);
      if (saved === "true" || saved === "false") {
        this._infiniteEnabled = saved === "true";
      }
    } catch (e) {}

    // If keyboard nav already active and user hasn't saved a pref, default OFF
    if (document.documentElement.classList.contains("is-keyboard-nav") && !this._hasSavedInfinitePref()) {
      this._infiniteEnabled = false;
    }

    this._setupInfiniteToggle();
    this._syncInfiniteToggleUI();
    this._updateInfiniteObserver();
    this._setupSortCtaSync();

    // Delegated click handling
    this.addEventListener("click", (e) => {
      const loadMore = e.target.closest("[data-load-more]");
      const viewCount = e.target.closest("[data-view-count]");

      if (loadMore) {
        e.preventDefault();
        if (this._isLoadingMore) return;
        this._loadMore({ triggeredBy: "button" });
        return;
      }

      if (viewCount) {
        this._updateColumnCount(viewCount.dataset.viewCount);
      }
    });

    // When keyboard starts, switch to button mode (unless user saved preference)
    window.addEventListener(
      "keydown",
      (e) => {
        if (e.key !== "Tab") return;
        if (this._keyboardModeApplied) return;
        this._keyboardModeApplied = true;

        if (this._hasSavedInfinitePref()) return;

        this._infiniteEnabled = false;
        this._syncInfiniteToggleUI();
        this._updateInfiniteObserver();

        const status = this._getStatusEl();
        if (status) {
          status.textContent =
            "Keyboard navigation detected. Infinite scroll is disabled. Use Load more to load additional products, or enable infinite scroll.";
        }
      },
      { passive: true },
    );

    // Restore columns
    try {
      const count = window.localStorage.getItem(COLUMNS_KEY) || DEFAULT_COLUMNS;
      this._updateColumnCount(count || DEFAULT_COLUMNS);
    } catch (error) {
      this._updateColumnCount(DEFAULT_COLUMNS);
    }

    const CARD_SELECTOR = "product-card, [data-product-card]";
    const SECONDARY_STOP = "[data-kbd-secondary]";
    const PRIMARY_LINK = "a[data-kbd-primary]";

    this.addEventListener(
      "focusin",
      (e) => {
        const card = e.target.closest(CARD_SELECTOR);
        if (!card) return;

        // If the explicit "Tab #2" stop is focused => show secondary
        if (e.target.matches(SECONDARY_STOP)) {
          card.setAttribute("data-kbd-view", "2");
          return;
        }

        // Otherwise (ANY other focus inside card) => force primary
        card.setAttribute("data-kbd-view", "1");
      },
      true, // capture helps when focus moves inside shadow/slots etc.
    );

    this.addEventListener(
      "focusout",
      (e) => {
        const card = e.target.closest(CARD_SELECTOR);
        if (!card) return;

        // If focus is leaving the card entirely, reset to primary
        const next = e.relatedTarget;
        if (!next || !card.contains(next)) {
          card.setAttribute("data-kbd-view", "1");
        }
      },
      true,
    );

    // If user Shift+Tabs back to image link, ensure primary
    this.addEventListener("keydown", (e) => {
      if (e.key !== "Tab") return;

      const card = getCardFromTarget(e.target);
      if (!card) return;

      // When focusing the primary media link, force primary image
      if (e.target.matches(KBD_PRIMARY_SELECTOR)) {
        setKbdView(card, 1);
      }
    });
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this._sortMutationObserver?.disconnect();
  }

  _hasSavedInfinitePref() {
    try {
      const saved = window.localStorage.getItem(INFINITE_KEY);
      return saved === "true" || saved === "false";
    } catch (e) {
      return false;
    }
  }

  _getStatusEl() {
    return this.querySelector("#plp-load-status");
  }

  _getFirstFocusable(root) {
    return root?.querySelector?.(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
    );
  }

  _setupSortCtaSync() {
    const toolbar = document.getElementById("searchspring-toolbar");
    if (!toolbar) return;

    const syncClearAllState = () => {
      const hasHashState = /\/(?:sort|filter):/i.test(window.location.hash);
      const url = new URL(window.location);
      const sortBy = url.searchParams.get("sort_by");
      const hasFilterParam = [...url.searchParams.keys()].some((k) =>
        k.startsWith("filter."),
      );

      const hasActive =
        hasHashState ||
        (sortBy && sortBy !== "manual") ||
        hasFilterParam;

      const clearAll = document.querySelector(".facets__clear-all");
      if (!clearAll) return;
      clearAll.classList.toggle("disabled", !hasActive);
    };

    document.addEventListener("click", (e) => {
      if (!e.target.closest(".facets__sort-option")) return;
      document
        .querySelector(".facets__apply-cta")
        ?.classList.remove("disabled");
      document
        .querySelector(".facets__clear-all")
        ?.classList.remove("disabled");
    });

    this._sortMutationObserver = new MutationObserver(syncClearAllState);
    this._sortMutationObserver.observe(toolbar, {
      childList: true,
      subtree: true,
    });

    syncClearAllState();
  }

  // ---------- Toggle ----------
  _setupInfiniteToggle() {
    this.infiniteToggle = this.querySelector("[data-infinite-toggle]");
    if (!this.infiniteToggle) return;

    this.infiniteToggle.addEventListener("click", (e) => {
      e.preventDefault();

      this._infiniteEnabled = !this._infiniteEnabled;

      try {
        window.localStorage.setItem(INFINITE_KEY, String(this._infiniteEnabled));
      } catch (e2) {}

      this._syncInfiniteToggleUI();
      this._updateInfiniteObserver();

      const status = this._getStatusEl();
      if (status) {
        status.textContent = this._infiniteEnabled
          ? "Infinite scroll enabled. Products will load automatically as you scroll."
          : "Infinite scroll disabled. Use the Load more button to load additional products.";
      }
    });
  }

  _syncInfiniteToggleUI() {
    if (!this.infiniteToggle) return;

    this.infiniteToggle.setAttribute("aria-pressed", this._infiniteEnabled ? "true" : "false");
    this.infiniteToggle.textContent = this._infiniteEnabled
      ? "Disable infinite scroll"
      : "Enable infinite scroll";
  }

  _updateInfiniteObserver() {
    this.observer?.disconnect();
    this.observer = null;

    if (!this._infiniteEnabled) return;
    if (!this.loadMoreWrapper) return;

    // Respect reduced motion: no auto-load
    if (window.matchMedia("prefers-reduced-motion").matches) return;

    this.observer = new IntersectionObserver((entries) => {
      if (entries[0]?.isIntersecting) {
        this._loadMore({ triggeredBy: "infinite" });
      }
    });

    this.observer.observe(this.loadMoreWrapper);
  }

  // ---------- Columns (unchanged from your logic) ----------
  _updateColumnCount(count) {
    try {
      window.localStorage.setItem(COLUMNS_KEY, count);
    } catch (error) {}

    this.style.setProperty("--plp-grid-columns", count);

    this.classList.forEach((className) => {
      if (className.startsWith("plp-columns-")) this.classList.remove(className);
    });
    this.classList.add(`plp-columns-${count}`);

    this.querySelectorAll("[data-view-count]").forEach((el) => {
      const isActive = el.dataset.viewCount === String(count);

      el.classList.toggle(
        "main-collection__view-count-button--active",
        isActive,
      );
      el.setAttribute("aria-current", isActive ? "true" : "false");
      const srText = el.querySelector(".visually-hidden");
      if (srText) {
        srText.textContent = isActive ? "Selected" : "";
      }
    });

    this._removeEditorialBlocks();
    this._insertEditorialBlocks();
  }

  _removeEditorialBlocks() {
    const blocks = this.querySelectorAll(".editorial-block");
    blocks.forEach((block) => block.remove());
  }

  _insertEditorialBlocks() {
    const editorialData = document.getElementById("editorial-data");
    if (!editorialData) return;

    const editorials = JSON.parse(editorialData.textContent);

    const activeButton = this.querySelector(".main-collection__view-count-button--active");
    const count =
      (activeButton ? parseInt(activeButton.dataset.viewCount, 10) : null) ||
      parseInt(getComputedStyle(this).getPropertyValue("--plp-grid-columns"), 10) ||
      4;

    const isMobile = window.innerWidth < 1024;
    const insertedIds = new Set();

    editorials
      .sort((a, b) => (isMobile ? a.mobileRow - b.mobileRow : a.desktopRow - b.desktopRow))
      .forEach((editorial) => {
        const row = isMobile ? editorial.mobileRow : editorial.desktopRow;
        if (!row) return;

        const key = `${editorial.id}-${isMobile ? "mobile" : "desktop"}`;
        if (insertedIds.has(key)) return;

        const alignment = editorial.alignment;
        const rightOffset = count === 2 ? 1 : 2;

        let slots;
        let index;
        if (isMobile) {
          index = (row - 1) * 2;
          slots = 2;
        } else {
          index = (row - 1) * count + (alignment === "right" ? rightOffset : 0);
          slots = count === 4 ? 4 : 1;
        }

        const cards = this.cards.flatMap((card) => {
          const cardSlots = parseInt(card.dataset.slots || "1", 10);
          return new Array(cardSlots).fill(card);
        });

        if (index > cards.length) return;

        const card = cards[index];
        if (!card) return;

        const block = document.createElement("div");
        block.className = `editorial-block editorial-block--${isMobile ? "mobile" : "desktop"} editorial-block--align-${alignment}`;
        block.innerHTML = `
          <a href="${editorial.url || "#"}">
            <img src="${isMobile ? editorial.mobile_image : editorial.desktop_image}" alt="">
          </a>
        `;
        block.dataset.slots = slots;
        card.before(block);
        insertedIds.add(key);
      });
  }

  get cards() {
    return [...this.querySelectorAll("[data-product-card], .editorial-block")];
  }

  // ---------- Load more ----------
  _loadMore({ triggeredBy = "button" } = {}) {
    const loadMore = this.querySelector("[data-load-more]");
    const productGrid = this.querySelector("[data-product-grid]");
    const status = this._getStatusEl();

    if (!loadMore || !productGrid) return;
    if (this._isLoadingMore) return;

    this._isLoadingMore = true;

    const beforeCount = productGrid.querySelectorAll("[data-product-card]").length;

    // Busy state + SR announcement
    loadMore.setAttribute("aria-busy", "true");
    loadMore.setAttribute("aria-disabled", "true");
    loadMore.classList.add("is-loading");
    loadMore.style.pointerEvents = "none";
    if (status) status.textContent = "Loading more products…";

    window.theme
      .fetch(loadMore.href)
      .then((response) => response.text())
      .then((response) => {
        const doc = new DOMParser().parseFromString(response, "text/html");
        const newCards = [...doc.querySelectorAll("[data-product-card]")];

        const newLoadMore = doc.querySelector("[data-load-more]");
        if (!newLoadMore) {
          loadMore.style.display = "none";
          this.observer?.disconnect();
        } else {
          loadMore.href = newLoadMore.href;
          loadMore.setAttribute("data-current-page", newLoadMore.getAttribute("data-current-page"));
          loadMore.innerHTML = newLoadMore.innerHTML;
        }

        productGrid.append(...newCards);

        this._removeEditorialBlocks();
        this._insertEditorialBlocks();

        if (status) {
          status.textContent = newCards.length
            ? `Loaded ${newCards.length} more products.`
            : "No more products to load.";
        }

        // Focus only for explicit button activation
        if (triggeredBy === "button" && newCards.length) {
          const allCardsNow = productGrid.querySelectorAll("[data-product-card]");
          const firstNewCard = allCardsNow[beforeCount];

          if (firstNewCard) {
            const focusTarget = this._getFirstFocusable(firstNewCard) || firstNewCard;

            if (focusTarget === firstNewCard && !firstNewCard.hasAttribute("tabindex")) {
              firstNewCard.setAttribute("tabindex", "-1");
              firstNewCard.addEventListener(
                "blur",
                () => firstNewCard.removeAttribute("tabindex"),
                { once: true },
              );
            }

            focusTarget.focus({ preventScroll: false });
          }
        }
      })
      .catch(() => {
        if (status) status.textContent = "Could not load more products.";
      })
      .finally(() => {
        loadMore.removeAttribute("aria-busy");
        loadMore.removeAttribute("aria-disabled");
        loadMore.classList.remove("is-loading");
        loadMore.style.pointerEvents = "";
        this._isLoadingMore = false;
      });
  }
}

if (!customElements.get("main-collection")) {
  customElements.define("main-collection", MainCollection);
}